import { isNil } from 'lodash';
import { useMemo } from 'react';
import {
  UseVisibleComponentsProps,
  useVisibleComponents,
} from '@shared/scenes/entity/util/useVisibleComponents';
import { API } from '@shared/types/api';

/**
 * Applies view-template component overrides to an array of components.
 * Non-null override fields (except `__typename`) are merged directly onto the component,
 * so consumers can check `component.required`, `component.isHidden`, etc. as effective values
 * without needing to consult `componentsViewOptions` separately.
 */
export function applyComponentOverrides(
  components: API.AnyComponent[],
  viewTemplate: Pick<API.ViewTemplate, 'componentsViewOptions'> | undefined,
  isReadOnly = false,
): API.AnyComponent[] {
  return components.map(component => {
    const viewOptions = viewTemplate?.componentsViewOptions?.find(
      option => option.componentId === component.id,
    );
    const componentReadOnly = 'readonly' in component ? component.readonly : undefined;
    const overrideReadOnly =
      !isNil(viewOptions?.overrides) && 'readonly' in viewOptions.overrides
        ? viewOptions.overrides.readonly
        : undefined;

    if (!isNil(viewOptions?.overrides)) {
      const preparedOverrides: Record<string, unknown> = {};
      Object.entries(viewOptions.overrides).forEach(([key, value]) => {
        if (value !== null && key !== '__typename') {
          preparedOverrides[key] = value;
        }
      });
      return {
        ...component,
        ...preparedOverrides,
        readonly: isReadOnly || componentReadOnly || overrideReadOnly,
      } as API.AnyComponent;
    }
    return {
      ...component,
      readonly: isReadOnly || componentReadOnly || overrideReadOnly,
    } as API.AnyComponent;
  });
}

export function useViewComponents({
  workflowTemplate,
  viewTemplate,
  includeServerManagedComponents = true,
  includeManagedByComponents = false,
  includeInputComponentsOnly = false,
  isReadOnly = false,
}: UseVisibleComponentsProps): API.AnyComponent[] {
  const visibleComponents = useVisibleComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents,
    includeManagedByComponents,
    includeInputComponentsOnly,
    isReadOnly,
  });

  return useMemo(
    () => applyComponentOverrides(visibleComponents, viewTemplate, isReadOnly),
    [visibleComponents, viewTemplate, isReadOnly],
  );
}
